const PATH_SEP = ' → ';

const TOKEN_ABBREVIATIONS = {
  rm: 'room',
  bldg: 'building',
  bld: 'building',
  fl: 'floor',
  lvl: 'level',
  bldng: 'building',
};

export const normalizeLocationText = (text) => {
  if (text == null || text === '') return '';
  let t = String(text).toLowerCase().trim();
  try {
    t = t.normalize('NFKD').replace(/\p{M}/gu, '');
  } catch {
    // ignore unsupported unicode normalization
  }
  t = t.replace(/([a-z])(\d)/g, '$1 $2').replace(/(\d)([a-z])/g, '$1 $2');
  t = t.replace(/[:_\-\/.>›]+/g, ' ');
  t = t.replace(/[^\w\s#]/g, ' ');
  return t.replace(/\s+/g, ' ').trim();
};

export const tokenizeLocationText = (text) => {
  const normalized = normalizeLocationText(text);
  if (!normalized) return [];
  return normalized.split(' ').filter((tok) => tok.length > 0);
};

const expandToken = (token) => TOKEN_ABBREVIATIONS[token] || token;

const levenshtein = (a, b) => {
  if (a === b) return 0;
  if (!a.length) return b.length;
  if (!b.length) return a.length;
  const rows = a.length + 1;
  const cols = b.length + 1;
  const matrix = Array.from({ length: rows }, () => new Array(cols).fill(0));
  for (let i = 0; i < rows; i += 1) matrix[i][0] = i;
  for (let j = 0; j < cols; j += 1) matrix[0][j] = j;
  for (let i = 1; i < rows; i += 1) {
    for (let j = 1; j < cols; j += 1) {
      const cost = a[i - 1] === b[j - 1] ? 0 : 1;
      matrix[i][j] = Math.min(
        matrix[i - 1][j] + 1,
        matrix[i][j - 1] + 1,
        matrix[i - 1][j - 1] + cost
      );
    }
  }
  return matrix[rows - 1][cols - 1];
};

const tokenMatches = (queryToken, targetToken) => {
  const q = expandToken(queryToken);
  const t = targetToken;
  if (!q || !t) return false;
  if (q === t) return true;
  if (t.startsWith(q) || q.startsWith(t)) return true;
  const maxDist = q.length <= 3 ? 1 : 2;
  return levenshtein(q, t) <= maxDist;
};

const allQueryTokensMatch = (queryTokens, targetTokens) => {
  if (!queryTokens.length) return true;
  return queryTokens.every((qt) => targetTokens.some((tt) => tokenMatches(qt, tt)));
};

const fuzzyTokenScore = (queryTokens, targetTokens) => {
  if (!queryTokens.length) return 1;
  let matched = 0;
  queryTokens.forEach((qt) => {
    if (targetTokens.some((tt) => tokenMatches(qt, tt))) matched += 1;
  });
  return matched / queryTokens.length;
};

export const getLocationName = (loc) =>
  loc?.zone_name || loc?.auto_label || loc?.name || loc?.label || '';

export const buildLocationFullPath = (loc, allLocations = []) => {
  const parts = [];
  if (loc?.ancestors?.length > 0) {
    loc.ancestors.forEach((a) => {
      const val = a?.value || a?.name;
      if (val) parts.push(String(val));
    });
  } else if (loc?.parent_id && allLocations.length > 0) {
    const byId = new Map(allLocations.map((l) => [l.id, l]));
    const chain = [];
    let current = loc;
    const visited = new Set();
    while (current && !visited.has(current.id)) {
      visited.add(current.id);
      chain.unshift(getLocationName(current));
      const parent = byId.get(current.parent_id);
      if (!parent) break;
      current = parent;
    }
    return chain.filter(Boolean).join(PATH_SEP);
  }
  const selfName = getLocationName(loc);
  if (selfName && (!parts.length || parts[parts.length - 1] !== selfName)) {
    parts.push(selfName);
  }
  [loc?.building, loc?.floor, loc?.room, loc?.corridor].forEach((field) => {
    const val = field ? String(field).trim() : '';
    if (val && !parts.includes(val)) parts.push(val);
  });
  return parts.filter(Boolean).join(PATH_SEP);
};

export const getLocationSearchBlob = (loc, allLocations = []) => {
  const fields = [
    loc?.auto_label,
    loc?.zone_name,
    loc?.zone_type,
    loc?.building,
    loc?.floor,
    loc?.room,
    loc?.corridor,
    loc?.description,
    loc?.label,
    buildLocationFullPath(loc, allLocations),
  ];
  (loc?.extra_details || []).forEach((ed) => {
    if (ed?.type) fields.push(ed.type);
    if (ed?.value) fields.push(ed.value);
  });
  return fields.filter(Boolean).join(' ');
};

export const scoreLocationSearch = (query, searchBlob) => {
  const normalizedQuery = normalizeLocationText(query);
  if (!normalizedQuery) return 1;

  const normalizedTarget = normalizeLocationText(searchBlob);
  if (!normalizedTarget) return 0;

  const queryTokens = tokenizeLocationText(query);
  const targetTokens = tokenizeLocationText(searchBlob);

  if (normalizedTarget === normalizedQuery) return 1000;
  if (normalizedTarget.startsWith(normalizedQuery)) return 800;
  if (allQueryTokensMatch(queryTokens, targetTokens)) return 600;
  if (normalizedTarget.includes(normalizedQuery)) return 400;

  const fuzzy = fuzzyTokenScore(queryTokens, targetTokens);
  if (fuzzy >= 0.5) return 200 + Math.round(fuzzy * 100);

  return 0;
};

export const locationMatchesQuery = (loc, query, allLocations = []) => {
  const blob = getLocationSearchBlob(loc, allLocations);
  return scoreLocationSearch(query, blob) > 0;
};

export const filterAndRankLocations = (locations, query, options = {}) => {
  const {
    allLocations = locations,
    parentId = undefined,
    limit,
    getSearchBlob,
  } = options;

  const safeLocations = Array.isArray(locations) ? locations : [];
  const locById = new Map(allLocations.map((l) => [l.id, l]));

  let pool = safeLocations;
  if (parentId !== undefined) {
    if (parentId === null || parentId === '') {
      pool = safeLocations.filter((l) => !l.parent_id || !locById.has(l.parent_id));
    } else {
      pool = safeLocations.filter((l) => String(l.parent_id || '') === String(parentId));
    }
  }

  const normalizedQuery = normalizeLocationText(query);
  if (!normalizedQuery) {
    const sorted = [...pool].sort((a, b) =>
      getLocationName(a).localeCompare(getLocationName(b))
    );
    return limit ? sorted.slice(0, limit) : sorted;
  }

  const scored = pool
    .map((loc) => {
      const blob = getSearchBlob ? getSearchBlob(loc, allLocations) : getLocationSearchBlob(loc, allLocations);
      return { loc, score: scoreLocationSearch(query, blob) };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return getLocationName(a.loc).localeCompare(getLocationName(b.loc));
    });

  const results = scored.map(({ loc }) => loc);
  return limit ? results.slice(0, limit) : results;
};

export const filterAndRankLocationItems = (items, query, getLabel) => {
  const normalizedQuery = normalizeLocationText(query);
  if (!normalizedQuery) return items;

  return [...items]
    .map((item) => {
      const label = getLabel(item) || '';
      const score = scoreLocationSearch(query, label);
      return { item, score };
    })
    .filter(({ score }) => score > 0)
    .sort((a, b) => {
      if (b.score !== a.score) return b.score - a.score;
      return String(getLabel(a.item)).localeCompare(String(getLabel(b.item)));
    })
    .map(({ item }) => item);
};
